"use client";

import { useState, useEffect, useCallback, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import dynamic from "next/dynamic";
import { SearchIcon, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { HEADER_NAV_TOP_CLASS } from "../../header-scroll-shadow";

const NAV_ICON_BTN =
	"inline-flex h-9 w-9 items-center justify-center rounded-lg border border-transparent text-foreground transition-colors hover:border-primary/30 hover:bg-primary/5 hover:text-primary lg:h-10 lg:w-10";

const CLOSE_DURATION_MS = 230;

const SearchInputWithSuggestions = dynamic(
	() => import("./search-input-with-suggestions").then((module) => module.SearchInputWithSuggestions),
	{
		ssr: false,
		loading: () => <div className="h-10 w-full rounded-lg bg-secondary" aria-busy="true" />,
	},
);

function useIsClient() {
	return useSyncExternalStore(
		() => () => {},
		() => true,
		() => false,
	);
}

export function SearchToggle({ channel, locale }: { channel: string; locale: string }) {
	const [isOpen, setIsOpen] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const mounted = useIsClient();
	const t = useTranslations("nav");

	const open = useCallback(() => {
		setIsClosing(false);
		setIsOpen(true);
	}, []);

	const close = useCallback(() => {
		setIsClosing(true);
		setTimeout(() => {
			setIsOpen(false);
			setIsClosing(false);
		}, CLOSE_DURATION_MS);
	}, []);

	useEffect(() => {
		if (!isOpen) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") close();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [isOpen, close]);

	const isVisible = isOpen || isClosing;

	return (
		<>
			<button type="button" onClick={open} className={NAV_ICON_BTN} aria-label={t("searchAria")}>
				<SearchIcon className="h-5 w-5" />
			</button>

			{mounted &&
				isVisible &&
				createPortal(
					<>
						{/* Backdrop */}
						<div
							role="presentation"
							onClick={close}
							className={`fixed bottom-0 left-0 right-0 z-[39] bg-black/30 backdrop-blur-[2px] ${HEADER_NAV_TOP_CLASS} ${isClosing ? "animate-backdrop-out" : "animate-backdrop-in"}`}
						/>

						{/* Search panel — sits below the sticky header */}
						<div
							className={`bg-background/95 fixed inset-x-0 z-[41] border-b border-border shadow-sm backdrop-blur-md ${HEADER_NAV_TOP_CLASS} ${isClosing ? "animate-search-panel-out" : "animate-search-panel-in"}`}
							role="search"
							aria-label="Produktsuche"
						>
							<div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
								<div className="flex items-center gap-3">
									<div className="flex-1">
										<SearchInputWithSuggestions channel={channel} locale={locale} autoFocus />
									</div>
									<button type="button" onClick={close} aria-label="Suche schließen" className={NAV_ICON_BTN}>
										<X className="h-5 w-5" />
									</button>
								</div>
							</div>
						</div>
					</>,
					document.body,
				)}
		</>
	);
}
